import React, { useState, useCallback, useEffect } from 'react';
import {
  Image,
  View,
  StyleSheet,
  ImageStyle,
  ViewStyle,
  ActivityIndicator,
  Text,
} from 'react-native';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { imageCache } from '@/utils/admin/imageCache';

interface LazyImageProps {
  source: { uri: string };
  style?: ImageStyle;
  containerStyle?: ViewStyle;
  resizeMode?: 'cover' | 'contain' | 'stretch' | 'repeat' | 'center';
  placeholder?: React.ReactNode;
  errorPlaceholder?: React.ReactNode;
  onLoad?: () => void;
  onError?: () => void;
}

export const LazyImage: React.FC<LazyImageProps> = ({
  source,
  style,
  containerStyle,
  resizeMode = 'cover',
  placeholder,
  errorPlaceholder,
  onLoad,
  onError,
}) => {
  const styles = useStyles();
  const { admin } = useAdminColors();
  const [loading, setLoading] = useState(!imageCache.isCached(source.uri));
  const [error, setError] = useState(false);

  useEffect(() => {
    if (imageCache.isCached(source.uri)) {
      setLoading(false);
      setError(false);
    } else {
      setLoading(true);
      setError(false);
      imageCache.markAsLoading(source.uri);
    }
  }, [source.uri]);

  const handleLoad = useCallback(() => {
    setLoading(false);
    setError(false);
    imageCache.markAsLoaded(source.uri);
    onLoad?.();
  }, [onLoad, source.uri]);

  const handleError = useCallback(() => {
    setLoading(false);
    setError(true);
    onError?.();
  }, [onError]);

  const handleLoadStart = useCallback(() => {
    if (!imageCache.isCached(source.uri)) {
      setLoading(true);
      setError(false);
      imageCache.markAsLoading(source.uri);
    }
  }, [source.uri]);

  const renderPlaceholder = () => {
    if (error) {
      return (
        errorPlaceholder || (
          <View style={styles.placeholder}>
            <Text style={styles.errorText}>Failed to load image</Text>
          </View>
        )
      );
    }

    if (loading) {
      return (
        placeholder || (
          <View style={styles.placeholder}>
            <ActivityIndicator size="small" color={admin.formInput.borderFocused} />
          </View>
        )
      );
    }

    return null;
  };

  return (
    <View style={[styles.container, style, containerStyle]}>
      {renderPlaceholder()}
      <Image
        source={source}
        style={[style, loading && styles.hidden, error && styles.hidden]}
        resizeMode={resizeMode}
        onLoad={handleLoad}
        onError={handleError}
        onLoadStart={handleLoadStart}
        progressiveRenderingEnabled={true}
        fadeDuration={200}
      />
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        position: 'relative',
      },
      placeholder: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: admin.dataTable.headerBg,
        justifyContent: 'center',
        alignItems: 'center',
      },
      hidden: {
        opacity: 0,
      },
      errorText: {
        fontSize: 12,
        color: colors.text.muted,
        textAlign: 'center',
      },
    });
  });
